// ---------------------------------------------------------------------------
// Billing client: thin authenticated wrappers over the backend's Stripe
// endpoints. Checkout and the customer portal are Stripe-hosted pages — the
// app only ever receives a redirect URL, never card data.
// ---------------------------------------------------------------------------

import { apiUrl } from '../config/cloud';
import { getAccessToken } from '../auth/auth';
import { setCredits } from './credits';

export interface BillingStatus {
  tier: 'free' | 'plus' | 'pro';
  subscriptionStatus: string | null;
  renewsAt: string | null;
  billingEnabled: boolean;
  hasStripeCustomer: boolean;
  credits: { used: number; allowance: number; periodStart: string };
}

export interface PlanInfo {
  id: 'free' | 'plus' | 'pro';
  name: string;
  priceUsd: number;
  aiCredits: number;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new Error('Please sign in first.');
  try {
    return await fetch(apiUrl(path), {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${token}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
  } catch {
    throw new Error('Could not reach the billing service — check your connection and try again.');
  }
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await authedFetch('/api/billing/status');
  if (!res.ok) throw new Error(`Could not load billing status (${res.status}).`);
  const status = (await res.json()) as BillingStatus;
  setCredits({
    used: status.credits.used,
    allowance: status.credits.allowance,
    exhausted: status.credits.used >= status.credits.allowance,
    tier: status.tier,
  });
  return status;
}

export async function getPlans(): Promise<PlanInfo[]> {
  const res = await authedFetch('/api/billing/plans');
  if (!res.ok) throw new Error(`Could not load plans (${res.status}).`);
  return ((await res.json()) as { plans: PlanInfo[] }).plans;
}

/** Start a Stripe Checkout session and redirect the browser to it. */
export async function startCheckout(plan: 'plus' | 'pro'): Promise<void> {
  const res = await authedFetch('/api/billing/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ plan }),
  });
  const body = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
  if (!res.ok || !body?.url) throw new Error(body?.error ?? `Could not start checkout (${res.status}).`);
  window.location.assign(body.url);
}

/** Open the Stripe customer portal (plan changes, invoices, cancellation). */
export async function openBillingPortal(): Promise<void> {
  const res = await authedFetch('/api/billing/portal', { method: 'POST' });
  const body = (await res.json().catch(() => null)) as { url?: string; error?: string } | null;
  if (!res.ok || !body?.url) throw new Error(body?.error ?? `Could not open the billing portal (${res.status}).`);
  window.location.assign(body.url);
}
